import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { CyclingEvent, eventCardModel, splitMonYear } from '@radevents/shared';

/**
 * Meine Events und Freunde im Profil (30.10.2026), Vorbild Future Pro und Runbuds (Mobbin).
 *
 * Drei Zahlen, die je auf ihre Ansicht führen (Vorgemerkt und Dabei auf Meine Events, Freunde auf
 * den Reiter Freunde), darunter das nächste Event. Vorher stand im Profil nur eine Zeile
 * „Meine Events und Freunde“ zwischen Passwort und Benachrichtigungen; die Planung, die seit
 * V198 das Herz des Kontos ist, kam im Profil nicht vor.
 *
 * `daten = null` heißt „lädt“: dann stehen die Kacheln ohne Zahl, damit nichts springt.
 */
export interface ProfilDaten {
  vorgemerkt: number;
  dabei: number;
  freunde: number;
  anfragen: number;
  naechstes: CyclingEvent | null;
}

const ProfilUebersicht: React.FC<{ daten: ProfilDaten | null }> = ({ daten }) => {
  const zahl = (n: number | undefined) => (daten ? n : ' ');
  const next = daten?.naechstes;
  const m = next ? eventCardModel(next) : null;

  return (
    <section className="pu" aria-label="Meine Events und Freunde">
      <div className="pu-kacheln">
        <Link to="/favorites" className="pu-kachel">
          <span className="pu-zahl">{zahl(daten?.vorgemerkt)}</span>
          <span className="pu-name">Vorgemerkt</span>
        </Link>
        <Link to="/favorites" className="pu-kachel">
          <span className="pu-zahl">{zahl(daten?.dabei)}</span>
          <span className="pu-name">Dabei</span>
        </Link>
        <Link to="/favorites?view=friends" className="pu-kachel">
          <span className="pu-zahl">{zahl(daten?.freunde)}</span>
          <span className="pu-name">Freunde</span>
          {!!daten?.anfragen && (
            <span className="pu-anfragen">{daten.anfragen === 1 ? '1 Anfrage' : `${daten.anfragen} Anfragen`}</span>
          )}
        </Link>
      </div>

      {next && m?.date && (
        <Link to={`/events/${next.id}`} className="pu-naechstes">
          {/* Dieselbe Kachel wie in den Event-Zeilen: eine Spanne („23–25“) passt dort in eine Zeile. */}
          <span className="ez-kachel" style={{ ['--ez-farbe' as string]: m.categoryColor }} aria-hidden>
            <span className="ez-monat">{splitMonYear(m.date.mon).month}</span>
            <span className={m.date.day.length > 2 ? 'ez-tag ez-tag--spanne' : 'ez-tag'}>{m.date.day}</span>
            <span className="ez-wochentag">{m.date.weekday ? m.date.weekday.slice(0, 2) : ''}</span>
          </span>
          <span className="pu-naechstes-text">
            <span className="pu-naechstes-kicker">{next.participating ? 'Als Nächstes dabei' : 'Als Nächstes vorgemerkt'}</span>
            <span className="pu-naechstes-titel">{next.title}</span>
            <span className="pu-naechstes-ort">{[next.city || m.region, m.countdown].filter(Boolean).join(' · ')}</span>
          </span>
          <ChevronRight size={18} className="pu-pfeil" aria-hidden />
        </Link>
      )}
      {daten && !next && (
        <Link to="/" className="pu-leer">
          <span>Noch nichts vorgemerkt. Events entdecken</span>
          <ChevronRight size={18} aria-hidden />
        </Link>
      )}
    </section>
  );
};

export default ProfilUebersicht;
